import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Title, PointElement, LineElement } from 'chart.js';
import { getAllCapsules } from '../../apis/capsuleAPI';
import './chartsPage.css';
import SidebarNav from '../../components/SidebarNav';

Chart.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Title, PointElement, LineElement);


const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        boxWidth: 12,
        padding: 20,
        font: {
          size: 12,
        },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(26, 28, 35, 0.9)',
      titleFont: {
        size: 13,
      },
      bodyFont: {
        size: 12,
      },
      padding: 10,
      cornerRadius: 4,
      displayColors: true,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
        drawBorder: false,
      },
      ticks: {
        font: {
          size: 11,
        },
      },
    },
    y: {
      grid: {
        borderDash: [2, 4],
        color: 'rgba(224, 224, 224, 0.3)',
        drawBorder: false,
      },
      ticks: {
        font: {
          size: 11,
        },
        maxTicksLimit: 6,
        stepSize: 1,
        callback: function(value) {
          if (value % 1 === 0) {
            return value;
          }
        }
      },
    },
  },
};

// Capsules by date chart
const lineChartOptions = {
  ...chartOptions,
  scales: {
    ...chartOptions.scales,
    y: {
      ...chartOptions.scales.y,
      ticks: {
        ...chartOptions.scales.y.ticks,
        stepSize: 1,
        callback: function(value) {
          if (value % 1 === 0) {
            return value;
          }
        }
      },
      beginAtZero: true
    }
  }
};

// Previous transcode chart, not using at this time
const transcodeChartOptions = {
  ...chartOptions,
  plugins: {
    ...chartOptions.plugins,
    legend: {
      display: true,
      position: 'bottom',
      onClick: function(e, legendItem, legend) {
        e.stopPropagation();
      },
      labels: {
        generateLabels: function(chart) {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map(function(label, i) {
              const dataset = data.datasets[0];
              const backgroundColor = dataset.backgroundColor[i];
              
              return {
                text: label,
                fillStyle: backgroundColor,
                strokeStyle: backgroundColor,
                lineWidth: 0,
                hidden: false,
                index: i
              };
            });
          }
          return [];
        },
        boxWidth: 12,
        padding: 20,
        font: {
          size: 12,
        },
      },
    },
  },
};



const ChartsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = location.state || JSON.parse(localStorage.getItem('currentUser'));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [capsules, setCapsules] = useState([]);
  const [filteredCapsules, setFilteredCapsules] = useState([]);
  const [issueData, setIssueData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Issue Types',
        data: [],
        backgroundColor: ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  });
  const [transcodeData, setTranscodeData] = useState({
    labels: ['Transcode Status'],
    datasets: [
      {
        label: 'Transcoded',
        data: [0],
        backgroundColor: '#7e3af2',
        borderWidth: 0,
        borderRadius: 4,
      },
      {
        label: 'Not Transcoded',
        data: [0],
        backgroundColor: '#1a1c23',
        borderWidth: 0,
        borderRadius: 4,
      }
    ],
  });
  const [downloadData, setDownloadData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Download Status',
        data: [],
        backgroundColor: ['#10b981', '#ef4444', '#f59e0b', '#3b82f6'],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  });
  const [barData, setBarData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Capsule Status',
        data: [],
        backgroundColor: ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  });
  const [dateLineData, setDateLineData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Capsules per Date',
        data: [],
        borderColor: '#7e3af2',
        backgroundColor: 'rgba(126, 58, 242, 0.1)',
        fill: true,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#7e3af2',
        pointBorderWidth: 2,
      },
    ],
  });
  
  const [latestTranscodeTimeData, setLatestTranscodeTimeData] = useState({
    labels:[],
    datasets:[
      {
        label: 'Latest Transcode Time per Date',
        data:[],
        borderColor: '#7e3af2',
        backgroundColor: 'rgba(126, 58, 242, 0.1)',
        fill: true,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#7e3af2',
        pointBorderWidth: 2,
      }
    ],
  });

  useEffect(() => {
    fetchCapsules();
  }, []);

  useEffect(() => {
    let filtered = capsules;
    
    if (startDate && endDate) {
      filtered = capsules.filter(capsule => {
        if (!capsule.transcode_date) return false;
        const capsuleDate = capsule.transcode_date.split('T')[0];
        return new Date(capsuleDate) >= new Date(startDate) && new Date(capsuleDate) <= new Date(endDate);
      });
    } else if (startDate) {
      filtered = capsules.filter(capsule => {
        if (!capsule.transcode_date) return false;
        const capsuleDate = capsule.transcode_date.split('T')[0];
        return new Date(capsuleDate) >= new Date(startDate);
      });
    } else if (endDate) {
      filtered = capsules.filter(capsule => {
        if (!capsule.transcode_date) return false;
        const capsuleDate = capsule.transcode_date.split('T')[0];
        return new Date(capsuleDate) <= new Date(endDate);
      });
    }
    
    setFilteredCapsules(filtered);
    calculateIssueData(filtered);
    calculateTranscodeData(filtered);
    calculateDownloadData(filtered);
    calculateBarData(filtered);
    calculateDateLineData(filtered);
    calculateLatestTranscodeTime(filtered, startDate, endDate);
  }, [startDate, endDate, capsules]);

  const fetchCapsules = useCallback(async () => {
    try {
      const response = await getAllCapsules({ page: 0, pageSize: 10000 });
      const data = response?.data?.capsules || [];
      // Sort capsules by transcode_date, newest to oldest
      const sortedCapsules = data.sort((a, b) => {
        if (!a.transcode_date) return 1;
        if (!b.transcode_date) return -1;
        return new Date(b.transcode_date) - new Date(a.transcode_date);
      });
      setCapsules(sortedCapsules);
      setFilteredCapsules(sortedCapsules);
    } catch (error) {
      console.error('Error fetching capsules:', error);
    }
  }, []);

  // Calculate issue data for the issue chart
  const calculateIssueData = (capsules) => {
    const issueCounts = capsules.reduce((acc, capsule) => {
      acc[capsule.status] = (acc[capsule.status] || 0) + 1;
      return acc;
    }, {});

    setIssueData({
      labels: Object.keys(issueCounts),
      datasets: [
        {
          label: 'Issue Types',
          data: Object.values(issueCounts),
          backgroundColor: ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'],
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    });
  };

  // Calculate transcode data for the transcode chart
  const calculateTranscodeData = (capsules) => {
    const transcodeStatusCounts = capsules.reduce((acc, capsule) => {
      if (capsule.status === 'No Issue' || capsule.status === 'Video Issue') {
        acc.transcoded += 1;
      } else {
        acc.notTranscoded += 1;
      }
      return acc;
    }, { transcoded: 0, notTranscoded: 0 });

    setTranscodeData({
      labels: ['Transcode Status'],
      datasets: [
        {
          label: 'Transcoded',
          data: [transcodeStatusCounts.transcoded],
          backgroundColor: '#7e3af2',
          borderWidth: 0,
          borderRadius: 4,
        },
        {
          label: 'Not Transcoded',
          data: [transcodeStatusCounts.notTranscoded],
          backgroundColor: '#1a1c23',
          borderWidth: 0,
          borderRadius: 4,
        }
      ],
    });
  };

  // Calculate download data for the download chart
  const calculateDownloadData = (capsules) => {
    const downloadStatusCounts = capsules.reduce((acc, capsule) => {
      const status = capsule.NBA_status;
      if (status && status !== "Unknown" && status.trim() !== "") {
        acc[status] = (acc[status] || 0) + 1;
      }
      return acc;
    }, {});

    setDownloadData({
      labels: Object.keys(downloadStatusCounts),
      datasets: [
        {
          label: 'Download Status',
          data: Object.values(downloadStatusCounts),
          backgroundColor: ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'],
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    });
  };

  // Calculate bar data with respect to capsule.status
  const calculateBarData = (capsules) => {
    const statusCounts = capsules.reduce((acc, capsule) => {
      acc[capsule.status] = (acc[capsule.status] || 0) + 1;
      return acc;
    }, {});

    setBarData({
      labels: Object.keys(statusCounts),
      datasets: [
        {
          label: 'Capsule Status',
          data: Object.values(statusCounts),
          backgroundColor: Object.keys(statusCounts).map((_, i) => 
            ['#ff5a1f', '#3f83f8', '#0e9f6e', '#7e3af2'][i % 4]),
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    });
  };

  // Calculate number of capsules per date for date chart
  const calculateDateLineData = (capsules) => {
    const dateCounts = capsules.reduce((acc, capsule) => {
      if (capsule.download_date) {
        const date = new Date(capsule.download_date).toLocaleDateString();
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    }, {});

    const sortedDates = Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b));
    
    setDateLineData({
      labels: sortedDates,
      datasets: [
        {
          label: 'Capsules per Date',
          data: sortedDates.map(date => dateCounts[date]),
          borderColor: '#7e3af2',
          backgroundColor: 'rgba(126, 58, 242, 0.1)',
          fill: true,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#7e3af2',
          pointBorderWidth: 2,
        },
      ],
    });
  };

  // Calculate latest transcode time of capsules for each date
  const calculateLatestTranscodeTime = (capsules, fromDate, toDate) => {
    // Map: date (YYYY-MM-DD) -> latest transcode_time (as string, e.g. '23:59:59')
    const latestTimesByDate = {};
    const startDateObj = fromDate ? new Date(fromDate) : null;
    const endDateObj = toDate ? new Date(toDate) : null;
    capsules.forEach(capsule => {
      if (capsule.transcode_date && capsule.transcode_time) {
        const date = capsule.transcode_date.split('T')[0];
        const capsuleDateObj = new Date(date);
        // Only consider dates in range if fromDate/toDate are provided
        if ((startDateObj && capsuleDateObj < startDateObj) || (endDateObj && capsuleDateObj > endDateObj)) return;
        // Compare times as strings (HH:MM:SS)
        if (!latestTimesByDate[date] || capsule.transcode_time > latestTimesByDate[date]) {
          latestTimesByDate[date] = capsule.transcode_time;
        }
      }
    });


    const sortedDates = Object.keys(latestTimesByDate).sort((a, b) => new Date(a) - new Date(b));
    // Prepare chart data
    setLatestTranscodeTimeData({
      labels: sortedDates,
      datasets: [
        {
          label: 'Latest Transcode Time',
          data: sortedDates.map(date => {
            // Convert HH:MM:SS to seconds for charting
            const t = latestTimesByDate[date].split(':');
            return parseInt(t[0], 10) * 3600 + parseInt(t[1], 10) * 60 + parseInt(t[2], 10);
          }),
          borderColor: '#7e3af2',
          backgroundColor: 'rgba(126, 58, 242, 0.1)',
          fill: true,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#7e3af2',
          pointBorderWidth: 2,
        },
      ],
    });
  };
  
// Navigate to the main page
  const handleHome = () => {
    navigate('/main', { state: { currentUser } });
  };
  
// Navigate to the user management page with respect to user role
  const handleUser = () => {
    console.log("Current user role is ", currentUser.role);
    if(["admin", "distributorAdmin"].includes(currentUser.role)) {
      navigate('/admin-user-management', { state: { currentUser } });
    } else if(["reviewer", "distributorRegular"].includes(currentUser.role)) {
      navigate('/reviewer-user-management', { state: { currentUser } });
    }
  };

  const handleTools = () => {
    navigate('/tools', { state: { currentUser } });
  };

// Select date range for the date chart
  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className={`charts-page-container theme-transition`}>
      <SidebarNav
        activePage="charts"
        onHome={handleHome}
        onTools={handleTools}
        onUsers={handleUser}
        onLogout={() => navigate('/login')}
      />

      <div className="main-content">
        <header className="header">
          <div>
            <h2 className="text-lg font-semibold">Welcome, {currentUser.firstName || currentUser.first_name}</h2>
          </div>
        </header>

        <div className="container space-y-6">
          <h2 className="page-title">Statistics</h2>

          <div className="date-filter-container">
            <div className="date-filter">
              <label htmlFor="date-range" className="filter-label">Filter by Date Range:</label>
              <div className="date-range-group">
                <div className="date-input-wrapper">
                  <label htmlFor="start-date" className="date-label">From:</label>
                  <input 
                    type="date" 
                    id="start-date" 
                    value={startDate} 
                    onChange={handleStartDateChange}
                    className="date-input"
                  />
                </div>
                
                <div className="date-input-wrapper">
                  <label htmlFor="end-date" className="date-label">To:</label>
                  <input 
                    type="date" 
                    id="end-date" 
                    value={endDate} 
                    onChange={handleEndDateChange}
                    className="date-input"
                  />
                </div>
                
                {(startDate || endDate) && (
                  <button onClick={clearDateFilter} className="clear-date-btn">
                    Clear
                  </button>
                )}
              </div>
              {(startDate || endDate) && (
                <div className="date-range-summary">
                  Showing data from
                  {startDate ? ` ${startDate} ` : ' earliest date '}
                  to
                  {endDate ? ` ${endDate}` : ' latest date'}
                  <span className="capsule-count"> ({filteredCapsules.length} capsules)</span>
                </div>
              )}
            </div>
          </div>

          <div className="chart-container">
            <div className="card chart-card">
              <div className="card-header">
                <h3 className="card-title">Capsules By Date</h3>
              </div>
              <div className="card-body">
                {dateLineData.labels.length > 0 && (
                  <Line 
                    data={dateLineData} 
                    options={lineChartOptions}
                    height={300}
                  />
                )}
              </div>
            </div>
            
            <div className="card chart-card">
              <div className="card-header">
                <h3 className="card-title">Latest Transcode Time Per Day</h3>
              </div>
              <div className="card-body">
                {latestTranscodeTimeData.labels.length > 0 && (
                  <Line
                    data={{
                      ...latestTranscodeTimeData,
                      datasets: [
                        {
                          ...latestTranscodeTimeData.datasets[0],
                          pointRadius: 4,
                        },
                      ],
                    }}
                    options={{
                      ...lineChartOptions,
                      plugins: {
                        ...lineChartOptions.plugins,
                        tooltip: {
                          ...lineChartOptions.plugins.tooltip,
                          callbacks: {
                            label: function(context) {
                              const value = context.parsed.y;
                              const h = Math.floor(value / 3600).toString().padStart(2, '0');
                              const m = Math.floor((value % 3600) / 60).toString().padStart(2, '0');
                              const s = (value % 60).toString().padStart(2, '0');
                              return `Latest: ${h}:${m}:${s}`;
                            }
                          }
                        }
                      },
                      scales: {
                        ...lineChartOptions.scales,
                        y: {
                          ...lineChartOptions.scales.y,
                          title: {
                            display: true,
                            text: 'Time (seconds since midnight)',
                          },
                          ticks: {
                            ...lineChartOptions.scales.y.ticks,
                            callback: function(value) {
                              // Convert seconds to HH:MM:SS
                              const h = Math.floor(value / 3600).toString().padStart(2, '0');
                              const m = Math.floor((value % 3600) / 60).toString().padStart(2, '0');
                              const s = (value % 60).toString().padStart(2, '0');
                              return `${h}:${m}:${s}`;
                            }
                          }
                        }
                      }
                    }}
                    height={300}
                  />
                )}
              </div>
            </div>
            
            <div className="card chart-card">
              <div className="card-header">
                <h3 className="card-title">Transcode Status</h3>
              </div>
              <div className="card-body">
                {transcodeData.labels.length > 0 && (
                  <Bar 
                    data={transcodeData} 
                    options={chartOptions}
                    height={300}
                  />
                )}
              </div>
            </div>
            
            <div className="card chart-card">
              <div className="card-header">
                <h3 className="card-title">Download Status</h3>
              </div>
              <div className="card-body">
                {downloadData.labels.length > 0 && (
                  <Pie 
                    data={downloadData} 
                    height={300}
                  />
                )}
              </div>
            </div>
            
            <div className="card chart-card">
              <div className="card-header">
                <h3 className="card-title">Capsule Status Distribution</h3>
              </div>
              <div className="card-body">
                {barData.labels.length > 0 && (
                  <Bar 
                    data={barData} 
                    options={chartOptions}
                    height={300}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartsPage;
